/* eslint-disable react/button-has-type */
import React, { FC, ReactNode } from "react";

import "./Button.scss";

export type ButtonProps = {
  /**
   * The size of the button. Can be 'sm' for small, 'md' for medium, or 'lg' for large.
   * Default is 'md'.
   */
  size?: "sm" | "md" | "lg";
  /**
   * An optional icon or element to be displayed on the left side of the button label.
   * Can be a React element or any valid React node.
   */
  iconLeft?: ReactNode;
  /**
   * An optional icon or element to be displayed on the right side of the button label.
   * Can be a React element or any valid React node.
   */
  iconRight?: ReactNode;
  /**
   * The text to be displayed on the button.
   */
  label?: ReactNode;
  /**
   * A callback function to be executed when the button is clicked.
   */
  onClick?: (e: any) => void;
  /**
   * Boolean indicating whether the button is disabled.
   * Default is false.
   */
  disabled?: boolean;
  /**
   * The variant of the button, determining its styling.
   * Can be one of the following: 'primary', 'secondary-gray', 'secondary-color', 'tertiary-gray', 'tertiary-color', 'link-gray', or 'link-color'.
   */
  variant?:
    | "primary"
    | "secondary-gray"
    | "secondary-color"
    | "tertiary-gray"
    | "tertiary-color"
    | "link-gray"
    | "link-color"
    | "link-error"
    | "destructive-primary"
    | "error";

  /**
   * The type of the button. Can be 'button', 'reset', or 'submit'.
   * Default is 'button'.
   */
  type?: "submit" | "reset" | "button";

  /**
   * id of the form this button submits. Lets a submit button live outside the
   * <form> element, e.g. in a Modal.Footer while the form is in Modal.Body.
   */
  form?: string;

  /**
   * custom classes for button component
   */
  className?: string;

  /**
   * custom classes for left icon component
   */
  leftIconClassName?: string;

  /**
   * custom classes for right icon component
   */
  rightIconClassName?: string;

  /**
   * custom classes for label component
   */
  labelClassName?: string;

  /** data-testid for the button element */
  testId?: string;

  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  "aria-expanded"?: boolean;
  "aria-haspopup"?: React.AriaAttributes["aria-haspopup"];
  "aria-controls"?: string;
  "aria-activedescendant"?: string;
  "aria-label"?: string;
};

const Button: FC<ButtonProps> = function ({
  size = "md",
  iconLeft,
  iconRight,
  label,
  disabled = false,
  variant = "primary",
  type = "button",
  form,
  className = "",
  leftIconClassName = "",
  rightIconClassName = "",
  labelClassName = "",
  testId,
  onClick,
  onFocus,
  onBlur,
  onKeyDown,
  "aria-expanded": ariaExpanded,
  "aria-haspopup": ariaHaspopup,
  "aria-controls": ariaControls,
  "aria-activedescendant": ariaActivedescendant,
  "aria-label": ariaLabel,
}) {
  const sizeClass = `v2-btn-${size}`;
  const variantClass = `v2-btn-${variant}`;
  const iconSizeClass = `v2-btn-icon-${size}`;

  return (
    <button
      className={`v2-btn ${sizeClass} ${variantClass} ${className}`}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      type={type}
      form={form}
      data-testid={testId}
      role={ariaActivedescendant ? "combobox" : undefined}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      aria-controls={ariaControls}
      aria-activedescendant={ariaActivedescendant}
      aria-label={ariaLabel}
    >
      {iconLeft && <span className={`v2-btn-icon v2-btn-icon-left ${iconSizeClass} ${leftIconClassName}`}>{iconLeft}</span>}
      <span className={`v2-btn-label ${labelClassName}`}>{label}</span>
      {iconRight && (
        <span className={`v2-btn-icon v2-btn-icon-right ${iconSizeClass} ${rightIconClassName}`}>{iconRight}</span>
      )}
    </button>
  );
};

Button.defaultProps = {
  variant: "primary",
  size: "md",
  disabled: false,
  type: "button",
  form: undefined,
  className: "",
  leftIconClassName: "",
  rightIconClassName: "",
  labelClassName: "",
  label: "",
  iconLeft: null,
  iconRight: null,
  onClick: () => {},
};

export default Button;
